import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useConfig } from '../../context/ConfigContext';
import { useTheme } from '../../context/ThemeContext';
import './Auth.css';

const Login = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const oauthCode = searchParams.get('code');
  const rawOauthState = String(searchParams.get('state') || '');
  const isOktaCallbackPath = location.pathname === '/auth/okta/callback';
  const isOktaFlow = isOktaCallbackPath || rawOauthState.startsWith('okta:');

  // The same choice the navbar makes, fallback included: the light wordmark is its own asset and
  // logo-original stands in if it fails to load.
  const { theme } = useTheme();
  const logoBase = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  const [lightLogoFailed, setLightLogoFailed] = useState(false);
  const logoSrc = theme === 'dark'
    ? `${logoBase}/logo/logo-white.png`
    : (lightLogoFailed ? `${logoBase}/logo/logo-original.png` : `${logoBase}/logo/logo-light.png`);
  const authLogo = (
    <img
      src={logoSrc}
      alt="GMS E-OPS"
      className="auth-logo"
      onError={() => setLightLogoFailed(true)}
    />
  );
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(() => Boolean(oauthCode && isOktaFlow));
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const { oktaLoginWithCode, login } = useAuth();
  const {
    oktaIssuer,
    oktaClientId,
    oktaAuthorizeUrl,
    oktaEnabled
  } = useConfig();

  const oktaRedirectUri = typeof window !== 'undefined' ? `${window.location.origin}/auth/okta/callback` : '';

  // Password sign-in, shown only when Okta is unavailable. This is the local-development path:
  // assertProductionSecurityConfig() refuses to boot without OKTA_ISSUER / CLIENT_ID / SECRET, so
  // oktaEnabled is always true in production and this branch cannot render there. The backend
  // restricts password login to admins regardless.
  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!username || !password) return;
    setError('');
    setLoading(true);
    try {
      await login(username, password);
    } catch (err) {
      setError((err && err.message) || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const startOktaLogin = () => {
    if (!oktaEnabled || !oktaClientId || !oktaRedirectUri) return;
    const authorizeUrl = oktaAuthorizeUrl || `${oktaIssuer}/v1/authorize`;
    if (!authorizeUrl) return;
    const nonce = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const state = `okta:${nonce}`;
    sessionStorage.setItem('okta_oauth_state', nonce);
    const params = new URLSearchParams({
      client_id: oktaClientId,
      response_type: 'code',
      scope: 'openid profile email',
      redirect_uri: oktaRedirectUri,
      state
    });
    window.location.assign(`${authorizeUrl}?${params.toString()}`);
  };

  useEffect(() => {
    const code = searchParams.get('code');
    if (!code || !isOktaFlow) return;
    const callbackUri = oktaRedirectUri;
    if (!callbackUri) return;

    const doneKey = `okta_code_exchanged_${code}`;
    const inflightKey = `okta_code_inflight_${code}`;
    if (sessionStorage.getItem(doneKey)) {
      setLoading(true);
      navigate('/home', { replace: true });
      return;
    }
    if (sessionStorage.getItem(inflightKey)) {
      setLoading(true);
      return;
    }

    const expectedNonce = sessionStorage.getItem('okta_oauth_state');
    const gotState = searchParams.get('state');
    const gotNonce = String(gotState || '').startsWith('okta:') ? String(gotState || '').slice(5) : '';
    if (!expectedNonce || !gotNonce || expectedNonce !== gotNonce) {
      setError('Invalid Okta login state. Please try again.');
      setLoading(false);
      return;
    }
    sessionStorage.removeItem('okta_oauth_state');
    sessionStorage.setItem(inflightKey, '1');

    setError('');
    setLoading(true);
    oktaLoginWithCode(code, callbackUri)
      .then((result) => {
        if (result.success) {
          setSearchParams({});
          sessionStorage.setItem(doneKey, '1');
          sessionStorage.removeItem(inflightKey);
          navigate('/home', { replace: true });
        } else {
          setError(result.error);
        }
      })
      .catch((err) => {
        setError(err?.response?.data?.error || err?.message || 'Okta SSO failed');
      })
      .finally(() => {
        sessionStorage.removeItem(inflightKey);
        setLoading(false);
      });
  }, [
    searchParams,
    isOktaFlow,
    oktaRedirectUri,
    navigate,
    setSearchParams,
    oktaLoginWithCode
  ]);

  // Never leave the user stuck on "Signing in with Okta…" forever. An interrupted exchange (tab
  // closed mid-flight, stale in-flight marker for a reused code, hung request) can otherwise spin
  // indefinitely. After a timeout, clear any stale in-flight markers and surface a retry.
  useEffect(() => {
    if (!(oauthCode && isOktaFlow && loading)) return undefined;
    const timer = window.setTimeout(() => {
      try {
        Object.keys(sessionStorage).forEach((k) => {
          if (k.startsWith('okta_code_inflight_')) sessionStorage.removeItem(k);
        });
      } catch (_) {
        /* sessionStorage may be unavailable */
      }
      setError('Sign-in is taking longer than expected. Please try signing in again.');
      setLoading(false);
    }, 30000);
    return () => window.clearTimeout(timer);
  }, [oauthCode, isOktaFlow, loading]);

  if (oauthCode && isOktaFlow && loading) {
    return (
      <div className="auth-container">
        <div className="auth-card auth-card--busy">
          {authLogo}
          <h1>Employee Operations</h1>
          <p className="auth-busy-message">Signing in with Okta…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        {authLogo}
        <h1>Employee Operations</h1>
        <h2>Login</h2>

        {oktaEnabled && oktaClientId ? (
          <div className="auth-sso-wrap">
            <button
              type="button"
              onClick={startOktaLogin}
              className="btn btn-okta"
              disabled={loading}
            >
              Sign in with Okta
            </button>
          </div>
        ) : (
          <form onSubmit={handlePasswordLogin} className="auth-form">
            <p className="auth-link">SSO is not configured — password sign-in (admins only).</p>
            <div className="form-group">
              <label htmlFor="login-username">Username or email</label>
              <input
                id="login-username"
                type="text"
                value={username}
                autoComplete="username"
                onChange={(e) => setUsername(e.target.value)}
                disabled={loading}
              />
            </div>
            <div className="form-group">
              <label htmlFor="login-password">Password</label>
              <input
                id="login-password"
                type="password"
                value={password}
                autoComplete="current-password"
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading || !username || !password}>
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        )}

        {error && <div className="error-message">{error}</div>}
      </div>
    </div>
  );
};

export default Login;
